@if (
  settings.isLoading() || isSaving() || isRunningUpdate() || isSavingCultures()
) {
  <mat-progress-bar
    mode="indeterminate"
    color="primary"
    class="top-progress"
  ></mat-progress-bar>
}

<div class="page-container">
  <mat-card>
    <mat-card-header>
      <mat-card-title>Configuração do ZARC</mat-card-title>
      <mat-card-subtitle>
        Parâmetros da API ZARC – Zoneamento Agrícola de Risco Climático
      </mat-card-subtitle>
    </mat-card-header>

    <mat-divider></mat-divider>

    <mat-card-content>
      <!-- Link obrigatório para instruções -->
      <div class="help-link-row">
        <mat-icon color="primary">help_outline</mat-icon>
        <a [href]="apiPdfUrl" target="_blank" rel="noopener noreferrer">
          Como criar conta e gerar nova chave da API ZARC
          <mat-icon class="external-icon">open_in_new</mat-icon>
        </a>
      </div>

      <form [formGroup]="form" (ngSubmit)="onSave()" autocomplete="off">
        <h3 class="section-title">Parâmetros Editáveis</h3>

        <!-- Chave da API -->
        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Chave da API ZARC (Embrapa)</mat-label>
          <input
            matInput
            [type]="showApiKey() ? 'text' : 'password'"
            formControlName="apiKey"
            autocomplete="new-password"
            placeholder="Cole aqui a chave gerada no portal da Embrapa"
          />
          <button
            matIconSuffix
            mat-icon-button
            type="button"
            (click)="showApiKey.set(!showApiKey())"
            [attr.aria-label]="showApiKey() ? 'Ocultar chave' : 'Exibir chave'"
            [matTooltip]="showApiKey() ? 'Ocultar chave' : 'Exibir chave'"
          >
            <mat-icon>{{
              showApiKey() ? "visibility_off" : "visibility"
            }}</mat-icon>
          </button>
          <mat-hint
            >Obtida em
            <a [href]="apiPdfUrl" target="_blank" rel="noopener noreferrer"
              >api.cnptia.embrapa.br</a
            >
            &mdash; Deixe em branco para manter a chave atual.</mat-hint
          >
        </mat-form-field>

        <!-- Tipo de Chave -->
        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Tipo de Chave da API</mat-label>
          <mat-select formControlName="apiKeyType" required>
            @for (t of apiKeyTypes; track t.value) {
              <mat-option [value]="t.value">
                {{ t.label }}
              </mat-option>
            }
          </mat-select>
          @if (form.controls.apiKeyType.hasError("required")) {
            <mat-error>Campo obrigatório.</mat-error>
          }
        </mat-form-field>

        <!-- Data de Geração -->
        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Data de Geração da Chave da API</mat-label>
          <input
            matInput
            [matDatepicker]="pickerGenerated"
            formControlName="apiKeyGeneratedAt"
            required
            readonly
          />
          <mat-datepicker-toggle
            matIconSuffix
            [for]="pickerGenerated"
          ></mat-datepicker-toggle>
          <mat-datepicker #pickerGenerated></mat-datepicker>
          @if (form.controls.apiKeyGeneratedAt.hasError("required")) {
            <mat-error>Campo obrigatório.</mat-error>
          }
        </mat-form-field>

        <mat-divider class="section-divider"></mat-divider>
        <h3 class="section-title">Informações de Controle (somente leitura)</h3>

        <!-- Data de Expiração (calculada) -->
        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Data de Expiração da Chave da API</mat-label>
          <input
            matInput
            [value]="
              apiKeyExpirationAt()
                ? apiKeyExpirationAt()!.setLocale('pt-BR').toLocaleString()
                : '—'
            "
            readonly
          />
          <mat-hint
            >Calculada automaticamente: data de geração + 30 dias</mat-hint
          >
        </mat-form-field>

        <!-- Data da Última Execução -->
        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Data da Última Atualização via API</mat-label>
          <input matInput [value]="lastRunAt" readonly />
          <mat-hint>Preenchido automaticamente pela execução do ZARC</mat-hint>
        </mat-form-field>

        <!-- Total de Requisições -->
        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Total de Requisições da Última Execução</mat-label>
          <input matInput [value]="lastRunRequestCount" readonly />
        </mat-form-field>

        <!-- Saldo Disponível -->
        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Saldo de Requisições Disponíveis</mat-label>
          <input matInput [value]="availableRequestBalance" readonly />
          <mat-hint
            >Limite do plano menos requisições já realizadas. Resetado ao
            cadastrar nova chave.</mat-hint
          >
        </mat-form-field>

        @if (
          settings.value()?.apiKeyType === "PAID" && availableRequestBalance < 0
        ) {
          <p class="balance-warning balance-warning--paid">
            <mat-icon>warning</mat-icon>
            Saldo excedido em {{ availableRequestBalance * -1 }} requisições. O
            excedente será cobrado conforme contrato do plano pago.
          </p>
        }
        @if (
          settings.value()?.apiKeyType === "FREE" &&
          availableRequestBalance <= 0
        ) {
          <p class="balance-warning balance-warning--free">
            <mat-icon>error</mat-icon>
            Saldo do plano gratuito esgotado (1.000 requisições/mês). Gere uma
            nova chave da API no portal da Embrapa e atualize as configurações
            acima.
          </p>
        }

        <div class="form-actions">
          <button
            mat-flat-button
            color="primary"
            type="submit"
            [disabled]="form.invalid || isSaving() || settings.isLoading()"
          >
            <mat-icon>save</mat-icon>
            Salvar Configurações
          </button>
        </div>
      </form>

      <mat-divider class="section-divider"></mat-divider>
      <h3 class="section-title">Dias de Subtração por Cultura</h3>
      <p class="section-description">
        Configure quantos dias serão subtraídos da data de emergência de cada
        cultura para o cálculo do risco ZARC (bval). Deixe em branco para usar
        0.
      </p>

      @if (cultures.isLoading()) {
        <p class="hint-text">Carregando culturas...</p>
      } @else if (!cultures.value()?.length) {
        <p class="hint-text">Nenhuma cultura cadastrada.</p>
      } @else {
        <div class="cultures-list">
          @for (culture of cultures.value()!; track culture.id) {
            <div class="culture-row">
              <span class="culture-name">{{ culture.description }}</span>
              <mat-form-field appearance="outline" class="field-subtraction">
                <mat-label>Dias (0–20)</mat-label>
                <input
                  matInput
                  type="number"
                  min="0"
                  max="20"
                  step="1"
                  [value]="editedSubtractionDays.get(culture.id) ?? ''"
                  (input)="
                    editedSubtractionDays.set(
                      culture.id,
                      $any($event.target).valueAsNumber || null
                    )
                  "
                />
                <mat-hint>Inteiro 0–20</mat-hint>
              </mat-form-field>
            </div>
          }
        </div>
      }

      <div class="form-actions">
        <button
          mat-flat-button
          color="primary"
          type="button"
          [disabled]="isSavingCultures() || cultures.isLoading()"
          (click)="onSaveAllCultureSubtractionDays()"
        >
          <mat-icon>{{
            isSavingCultures() ? "hourglass_empty" : "save"
          }}</mat-icon>
          {{ isSavingCultures() ? "Salvando..." : "Salvar Dias de Subtração" }}
        </button>
      </div>

      <mat-divider class="section-divider"></mat-divider>
      <h3 class="section-title">Atualização da Base de Zoneamento</h3>

      <div class="form-actions">
        <button
          mat-flat-button
          color="accent"
          type="button"
          [disabled]="isRunningUpdate() || settings.isLoading()"
          (click)="onRunUpdate()"
          [matTooltip]="
            isRunningUpdate()
              ? 'Atualização em andamento...'
              : 'Consulta a API ZARC e atualiza a base de zoneamento'
          "
        >
          <mat-icon>{{
            isRunningUpdate() ? "hourglass_empty" : "sync"
          }}</mat-icon>
          {{
            isRunningUpdate()
              ? "Atualizando..."
              : "Atualizar Base de Zoneamento"
          }}
        </button>
      </div>
    </mat-card-content>
  </mat-card>
</div>
